import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "axios";
import TableFilter from "src/components/TableFilter";
import DateUtils from "src/services/utils/DateUtils";
import { API_BASE_URL } from "src/modules/3rd-party-management/constants";

const FilteringComponent = ({
    t,
    token,
    onChange,
}) => {
    const [tableFilters, setTableFilters] = useState({
        dateFrom: null,
        dateTo: null,
        supplierId: null,
    });

    const [suppliersFilter, setSuppliersFilter] = useState({
        pageSize: 50,
        pageIndex: 1,
        name: "",
    });

    const initRef = useRef(false);
    const dateUtils = new DateUtils();

    const {
        isFetching: fetchingSuppliers,
        data: suppliers,
        refetch: refetchSuppliers,
    } = useQuery({
        queryKey: [
            "3rd-party-management-license-usage-suppliers-filter",
            new URLSearchParams(
                Object.fromEntries(
                    Object.entries(suppliersFilter).filter(([_key, value]) => {
                        return !value ? false : (Array.isArray(value) ? value.length > 0 : true);
                    })
                )
            ).toString(),
        ],
        queryFn: async () => {
            const response = await axios.get(`${API_BASE_URL}/organization/providers`, {
                params: suppliersFilter,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data.data;
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching third parties list."), {
                type: "error",
            });
        },
    });

    const tableFilterTabs = useMemo(() => {
        return [
            {
                type: "date",
                name: "filter-date",
                title: "Filter by date",
                icon: <i className="ri-calendar-event-line" />,
                defaultValue: {
                    from: tableFilters.dateFrom,
                    to: tableFilters.dateTo,
                },
            },
            {
                type: "radio_list",
                name: "supplierId",
                title: "Supplier",
                icon: <i className="ri-team-line" />,
                isLoading: fetchingSuppliers,
                defaultValue: tableFilters?.supplierId,
                data: suppliers || {},
                searchInListOption: {
                    enabled: true,
                    remote: true,
                    defaultValue: suppliersFilter?.name,
                    handler: (value) => {
                        setSuppliersFilter((currentState) => ({
                            ...currentState,
                            pageIndex: 1,
                            name: value,
                        }));
                    },
                },
                pagination: {
                    enabled: true,
                    totalCountKey: "itemsCount",
                    pageIndexKey: "pageIndex",
                    pageSizeKey: "pageSize",
                    dataKey: "thirdParties",
                    handler: ({ pageIndex, pageSize }) => {
                        setSuppliersFilter((currentState) => ({
                            ...currentState,
                            pageIndex,
                            pageSize,
                        }));
                    },
                },
                formatter: (item) => item.name,
            },
        ];
    }, [
        tableFilters,
        fetchingSuppliers,
        suppliersFilter,
        suppliers,
        t,
    ]);

    const onTableFilterOkButtonClicked = (filters) => {
        if (filters) {
            const result = {};

            if ("filter-date" in filters) {
                result.dateFrom = dateUtils.convertDateToDate(
                    new Date(filters["filter-date"]?.from),
                    "YYYY-MM-DD"
                );

                result.dateTo = dateUtils.convertDateToDate(
                    new Date(filters["filter-date"]?.to),
                    "YYYY-MM-DD"
                );
            }

            if ("supplierId" in filters) {
                result.supplierId =
                    filters.supplierId !== "all" ? filters.supplierId : undefined;
            }

            setTableFilters({
                ...tableFilters,
                ...result,
            });
        } else {
            setTableFilters({
                dateFrom: null,
                dateTo: null,
                supplierId: null,
            });
        }
    };

    useEffect(() => {
        refetchSuppliers();
    }, [suppliersFilter]);

    useEffect(() => {
        if (!initRef.current) {
            initRef.current = true;
            return;
        }

        onChange && onChange(tableFilters);
    }, [tableFilters, onChange]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab="filter-date"
            tabs={tableFilterTabs}
            onOkButtonClicked={onTableFilterOkButtonClicked}
        />
    );
};

const mapStateToProps = (state) => {
    const { token } = state.Login;

    return { token };
};

export default withNamespaces()(
    connect(mapStateToProps, null)(FilteringComponent)
);
